import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaCheck, FaArrowRight } from "react-icons/fa6";
import { Sparkles, Shield, Zap, Building2, Calendar } from "lucide-react";
import Button from "../../../components/ui/Button";
import useNavigateToFibiVoice from "../../../../../hooks/useNavigateToFibiVoice";

interface PricingProps {
  activeTab: "individuals" | "businesses";
  setActiveTab: (tab: "individuals" | "businesses") => void;
}

const Pricing: React.FC<PricingProps> = ({ activeTab, setActiveTab }) => {
  const { url } = useNavigateToFibiVoice();

  const handleStartPractice = () => {
    window.open(url, "_blank");
  };

  const handleContactBusiness = () => {
    window.open(
      "https://cal.com/seamlessvisa/consultation",
      "_blank",
    );
  };

  return (
    <section
      id="pricing-section"
      className="py-20 sm:py-24 bg-white overflow-hidden scroll-mt-20"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-flex items-center gap-1.5 bg-blue-ea text-blue-00 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-widest mb-4">
            ✦ Simple, Transparent Pricing
          </span>
          <h2 className="text-4xl md:text-5xl font-light font-lato text-black-1a leading-tight">
            Choose the Plan that Fits How You Use{" "}
            <span className="text-blue-00 italic mx-1">FiBi</span>
          </h2>
          <p className="mt-6 text-lg text-dark-6b max-w-xl mx-auto leading-relaxed">
            Get high-fidelity interview training with realistic Consular
            questions, Text & Voice simulations, and instant analytical
            feedback.
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex justify-center mb-16">
          <div className="relative flex p-1.5 bg-surface-alt rounded-2xl border border-border">
            <button
              onClick={() => setActiveTab("individuals")}
              className={`relative z-10 px-6 py-2.5 text-sm font-bold rounded-xl transition-colors duration-300 ${
                activeTab === "individuals"
                  ? "text-white"
                  : "text-text-muted hover:text-black-1a"
              }`}
            >
              For Individuals
              {activeTab === "individuals" && (
                <motion.div
                  layoutId="activeTabBg"
                  className="absolute inset-0 bg-purple-a8 rounded-xl -z-10"
                  transition={{ type: "spring", stiffness: 380, damping: 30 }}
                />
              )}
            </button>
            <button
              onClick={() => setActiveTab("businesses")}
              className={`relative z-10 px-6 py-2.5 text-sm font-bold rounded-xl transition-colors duration-300 ${
                activeTab === "businesses"
                  ? "text-white"
                  : "text-text-muted hover:text-black-1a"
              }`}
            >
              For Businesses
              {activeTab === "businesses" && (
                <motion.div
                  layoutId="activeTabBg"
                  className="absolute inset-0 bg-teal-00 rounded-xl -z-10"
                  transition={{ type: "spring", stiffness: 380, damping: 30 }}
                />
              )}
            </button>
          </div>
        </div>

        {/* Pricing Content */}
        <div className="relative min-h-[460px]">
          <AnimatePresence mode="wait">
            {activeTab === "individuals" ? (
              <motion.div
                key="individuals"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.4 }}
                className="max-w-5xl mx-auto"
              >
                {/* Individuals intro (Redesign doc §4.7a) */}
                <p className="text-base text-dark-6b leading-relaxed text-center max-w-3xl mx-auto mb-12">
                  FiBi is ideal for students, tourists, workers, and families who
                  want flexible, on-demand interview practice before meeting the
                  consular officer. Choose how you access FiBi: purchase single
                  pay-as-you-go sessions in any quantity, or subscribe monthly for
                  unlimited access throughout the subscription period.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
                {/* Pay As You Go */}
                <motion.div
                  whileHover={{ y: -6 }}
                  className="relative flex flex-col p-8 sm:p-10 rounded-3xl border border-border bg-white shadow-card hover:shadow-card-lg transition-all duration-300"
                >
                  <div className="mb-8">
                    <div className="inline-flex rounded-xl bg-blue-ea p-2.5 text-blue-00! mb-4">
                      <Shield className="w-5 h-5" />
                    </div>
                    <h3 className="text-2xl font-light font-lato text-black-1a">
                      Pay As You Go
                    </h3>
                    <p className="mt-2 text-sm text-dark-6b">
                      Perfect for quick, targeted practice rounds.
                    </p>
                    <div className="flex items-baseline gap-1 mt-6">
                      <span className="text-5xl font-light font-lato text-black-1a">
                        $2.99
                      </span>
                      <span className="text-sm font-bold text-text-muted">
                        / session
                      </span>
                    </div>
                  </div>

                  <ul className="flex-1 space-y-4 mb-10">
                    {[
                      "1 Full-Length Consular Mock Session",
                      "U.S. Visa Category-Specific Questions",
                      "Text & Voice Simulation Modes",
                      "Immediate Session Performance Score",
                      "AI-Powered Corrections & Tips",
                    ].map((feat, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="mt-1 shrink-0 w-5 h-5 rounded-full bg-blue-ea text-blue-00! flex items-center justify-center">
                          <FaCheck className="w-3 h-3" />
                        </div>
                        <span className="text-sm text-dark-6b">{feat}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="flex justify-start">
                    <Button
                      label="Start Preparing Now"
                      onClick={handleStartPractice}
                      variant="primary"
                      size="large"
                      endIcon={<FaArrowRight />}
                    />
                  </div>
                </motion.div>

                {/* Monthly Subscription */}
                <motion.div
                  whileHover={{ y: -6 }}
                  className="relative flex flex-col p-8 sm:p-10 rounded-3xl border-2 border-blue-00 bg-blue-00/5 shadow-spotlight hover:shadow-card-lg transition-all duration-300 overflow-hidden"
                >
                  <div className="absolute top-0 right-0 bg-blue-00 text-white px-5 py-1.5 rounded-bl-3xl text-xs font-bold uppercase tracking-widest shadow-md flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    Best Value
                  </div>

                  <div className="mb-8">
                    <div className="inline-flex rounded-xl bg-blue-ea text-blue-00 p-2.5 mb-4">
                      <Zap className="w-5 h-5" />
                    </div>
                    <h3 className="text-2xl font-light font-lato text-black-1a">
                      Monthly Subscription
                    </h3>
                    <p className="mt-2 text-sm text-dark-6b">
                      For applicants committed to getting approved.
                    </p>
                    <div className="flex items-baseline gap-1 mt-6">
                      <span className="text-5xl font-light font-lato text-blue-00">
                        $19.99
                      </span>
                      <span className="text-sm font-bold text-text-muted">
                        / month
                      </span>
                    </div>
                  </div>

                  <ul className="flex-1 space-y-4 mb-10">
                    {[
                      "Unlimited Mock Interview Sessions",
                      "Priority AI Speech Recognition Model",
                      "Full Performance History & Dashboard",
                      "Detailed Skill-specific Analytics PDF Reports",
                      "Interactive Pitch & Hesitation Analysis",
                      "24/7 Unlimited Access with Voice + Text",
                    ].map((feat, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="mt-1 shrink-0 w-5 h-5 rounded-full bg-blue-ea text-blue-00 flex items-center justify-center">
                          <FaCheck className="w-3 h-3" />
                        </div>
                        <span className="text-sm text-black-31 font-medium">
                          {feat}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <div className="flex justify-start">
                    <Button
                      label="Get Premium Access"
                      onClick={handleStartPractice}
                      variant="fibi"
                      size="large"
                      endIcon={<FaArrowRight />}
                    />
                  </div>
                </motion.div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="businesses"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.4 }}
                className="max-w-4xl mx-auto"
              >
                {/* B2B Spotlight Bento Card */}
                <div className="relative overflow-hidden rounded-3xl border border-blue-ea bg-linear-to-br from-white to-blue-ea/20 p-8 sm:p-12 shadow-card hover:shadow-card-lg transition-all duration-300">
                  <div className="absolute top-[-10%] right-[-10%] w-[30%] h-[30%] bg-blue-ea/30 rounded-full blur-[80px] pointer-events-none" />

                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                    <div className="lg:col-span-7 space-y-6">
                      <div className="inline-flex rounded-xl bg-blue-ea text-blue-00 p-3">
                        <Building2 className="w-6 h-6" />
                      </div>
                      <h3 className="text-3xl font-bold font-spartan text-black-1a leading-tight">
                        Power training at scale for your agency
                      </h3>
                      <p className="text-base text-dark-6b leading-relaxed">
                        FiBi also powers training at scale for education consultants, immigration consultants, travel agencies, attorneys, visa advisors, and HR teams preparing their own clients for U.S. visa interviews. Business plans are subscription-based, and they grant a set number of session credits distributed across the business's client base.
                      </p>
                      <p className="text-base text-dark-6b leading-relaxed">
                        Sessions are consumed when a client of the subscribing business completes a FiBi mock interview. The business account holder manages credit allocation and monitors clients' usage via a dedicated admin dashboard.
                      </p>
                      <p className="text-base text-dark-6b leading-relaxed">
                        Preparation is the key to confidence, and with FiBi, you can provide that confidence to every client you serve.
                      </p>

                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {[
                          "Client consumption tracking",
                          "Bulk session bundles",
                          "Custom branding modules",
                          "Detailed progress insights",
                        ].map((feat, idx) => (
                          <li key={idx} className="flex items-center gap-2.5">
                            <div className="shrink-0 w-5 h-5 rounded-full bg-blue-ea text-blue-00 flex items-center justify-center">
                              <FaCheck className="w-3 h-3" />
                            </div>
                            <span className="text-sm font-semibold text-black-31">
                              {feat}
                            </span>
                          </li>
                        ))}
                      </ul>

                      <div className="pt-4 flex flex-col sm:flex-row items-center gap-4">
                        <Button
                          label="Talk to us about FiBi for Businesses"
                          onClick={handleContactBusiness}
                          variant="secondary"
                          size="large"
                          startIcon={<Calendar className="w-5 h-5" />}
                          iconHoverStyle="scale"
                        />
                      </div>
                    </div>

                    {/* Right Decorative Graphic for Business */}
                    <div className="lg:col-span-5 relative flex justify-center">
                      <div className="relative w-full max-w-[280px] p-6 rounded-2xl bg-white border border-border shadow-2xl space-y-4">
                        <div className="flex justify-between items-center border-b border-border pb-3">
                          <span className="text-xs font-bold text-text-muted uppercase tracking-wider">
                            Agency Dashboard
                          </span>
                          <span className="text-[10px] font-bold text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                            ACTIVE
                          </span>
                        </div>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center text-xs">
                            <span className="text-dark-6b">Total Clients:</span>
                            <span className="font-bold text-black-1a">142</span>
                          </div>
                          <div className="flex justify-between items-center text-xs">
                            <span className="text-dark-6b">
                              Credits Remaining:
                            </span>
                            <span className="font-bold text-blue-00">
                              1,250
                            </span>
                          </div>
                          <div className="flex justify-between items-center text-xs">
                            <span className="text-dark-6b">
                              Avg. Readiness:
                            </span>
                            <span className="font-bold text-blue-00!">
                              84/100
                            </span>
                          </div>
                        </div>
                        <div className="pt-2">
                          <div className="h-2 w-full bg-surface-alt rounded-full overflow-hidden">
                            <div
                              className="h-full bg-blue-00 rounded-full"
                              style={{ width: "84%" }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
